import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '@environment';
import { Customer } from '@shared/models/customer';

import { StorageService } from '@core/services/util/storage.service';
import { UtilityService } from '@core/services/util/utility.service';
import { LoginService } from '@core/services/login/login.service';
import { CartService } from '@core/services/cart/cart.service';
import { CustomerApiService } from '@core/http/customer-api.service';
import { AddressService } from '@core/services/address/address.service';
import { ToastrService } from '../notification/toastr.service';
import { PassService } from '@core/services/pass/pass.service';

export interface LoginDetails {
    email: string;
    password: string;
}

@Injectable({
    providedIn: 'root'
})
export class CustomerService {
    baseUrl: string;
    c3pServiceUrl: string;
    accountsApiUrl: string;
    customer = new BehaviorSubject<Customer>(new Customer());

    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private loginService: LoginService,
        private utilityService: UtilityService,
        private router: Router,
        private cartService: CartService,
        private customerApiService: CustomerApiService,
        private addressService: AddressService,
        private toastrService: ToastrService,
        private passService: PassService
    ) {
        this.baseUrl = environment.apiUrl;
        this.accountsApiUrl = environment.accountsApiUrl;
        this.c3pServiceUrl = environment.c3pServiceUrl;
    }

    getCustomer(performAfterLoginTask: boolean = false) {
        const headers = this.utilityService.getAuthHeaders('signed');
        const customer_token = this.storageService.get('customer_token');

        lastValueFrom(
            this.http.post(
                this.baseUrl + '/v3/customer/customer_details',
                {
                    customer_token: customer_token || ''
                },
                {
                    headers
                }
            )
        )
            .then((data: any) => {
                this.setCustomerDetails(data, performAfterLoginTask);
            })
            .catch(() => {
                this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
                this.afterLogoutTask();
                this.loginService.clearSessions();
            });
    }

    getBottomStripsApi() {
        lastValueFrom(this.customerApiService.getBottomStrips())
            .then((data) => {
                if (data.meta.code !== 200) {
                    return;
                }
                this.updateCustomerDetails(data);
            })
            .catch((e: any) => {
                console.log(e);
            });
    }

    updateCustomerDetails({ new_customer, last_order_number_of_days }) {
        this.storageService.set('new_customer', String(new_customer));
        this.storageService.set('last_order_number_of_days', String(last_order_number_of_days));
    }

    setAuthTokenAndFetchDetails(data: any) {
        const expiryDate = this.utilityService.getDefaultCookieExpiryTime();

        if (data.auth_token) {
            this.storageService.set('session-id', data.auth_token, expiryDate);
        }

        //handled the case when customer_token is undefined
        const customer_token = !data.customer_token ? '' : data.customer_token;
        this.storageService.set('customer_token', customer_token, expiryDate);

        this.getCustomer(true);
    }

    private setCustomerDetails(data: any, performAfterLoginTask: boolean = true) {
        this.customer.next(data.customer);
        this.loginService.setCustomerDetails(data.customer, performAfterLoginTask);
    }

    updatePersonalInfo(name: string, email: string, birthday: string) {
        return this.http
            .put(
                this.accountsApiUrl + '/customers/update',
                {
                    name: name,
                    email: email,
                    customer_info: {
                        birthday: birthday
                    }
                },
                {
                    headers: this.utilityService.getAuthHeaders('signed')
                }
            )
            .pipe(
                map((resp: any) => {
                    if (resp && resp.meta && resp.meta.code === 200) {
                        if (resp.customer_token) {
                            const expiryDate = this.utilityService.getDefaultCookieExpiryTime();
                            this.storageService.set('customer_token', resp.customer_token, expiryDate);
                        }
                        this.getCustomer();
                    }
                    return resp;
                })
            );
    }

    updateSubscription(allow_sms: boolean, allow_mail: boolean, allow_push: boolean) {
        return this.http.get(this.baseUrl + '/customer/subscribe', {
            params: {
                allow_sms: String(allow_sms),
                allow_mail: String(allow_mail),
                allow_push: String(allow_push)
            },
            headers: this.utilityService.getAuthHeaders('signed')
        });
    }

    updatePassword(passwordDetails: { old_password: string; password: string; password_confirmation: string }) {
        return this.http.put(this.accountsApiUrl + '/customers/update_password', passwordDetails, {
            headers: this.utilityService.getAuthHeaders('signed')
        });
    }

    creditDetails() {
        const outlet_id = this.storageService.get('outlet_id') || 1;

        return this.http.get(this.c3pServiceUrl + '/customer_credit/credit_details', {
            params: {
                outlet_id
            },
            headers: this.utilityService.getAuthHeaders()
        });
    }

    updateDeviceToken(token: any) {
        return this.http.put(
            this.baseUrl + '/customer/token',
            {
                device_token: token
            },
            {
                headers: this.utilityService.getAuthHeaders()
            }
        );
    }

    coupons() {
        return this.http.get(this.c3pServiceUrl + '/coupons', {
            headers: this.utilityService.getAuthHeaders()
        });
    }

    afterLogoutTask() {
        this.loginService.clearAllData();
        this.addressService.addresses.next([]);
        this.addressService.setLocality();
        this.cartService.resetServerCart();
        this.passService.resetCustomerPasses();
        this.cartService.updatePrices();
        this.navigateTo('/');
    }

    logOut() {
        this.loginService
            .logOutApi()
            .toPromise()
            .then((data: any) => {
                if (data && data.meta && data.meta.code === 200) {
                    const expiryDate = this.utilityService.getDefaultCookieExpiryTime();
                    this.storageService.set('unsigned_session_id', data.unsigned_auth_token, expiryDate);
                }
            })
            .finally(() => {
                this.afterLogoutTask();
            });
    }

    navigateTo(url: string) {
        this.router.navigateByUrl(url);
    }
}
